import * as React from 'react';
import { ReactElement } from 'react'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import Icon from 'src/@core/components/icon'
import Popover from '@mui/material/Popover';

// ** Types
import { ThemeColor } from 'src/@core/layouts/types'

// ** Custom Components Imports
import CustomAvatar from 'src/@core/components/mui/avatar'
import CardSalesInCountries from 'src/@core/components/dashboard/UpcomingReminders'
import AllUsersOverview from 'src/@core/components/dashboard/AllUsersOverview';
import UpcomingReminders from 'src/@core/components/dashboard/UpcomingReminders';
import { CircularProgress, Tooltip, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ProtectedRoute from 'src/context/ProtectedRoute';
import { useAuth } from 'src/hooks/useAuth';
import { useGetNumberOfCompanies } from 'src/hooks/dashboard/useGetNumberOfCompanies';
import { useGetNumberOfDrivers } from 'src/hooks/dashboard/useGetNumberOfDrivers';
import { useGetNumberOfVehicles } from 'src/hooks/dashboard/useGetNumberOfVehicles';
import { useGetNumberOfEmployees } from 'src/hooks/dashboard/useGetNumberOfEmployees';

const Home = () => {
  const calculateDaysUntilDue = () => {
    const now = new Date();
    const taxDay = new Date(now.getFullYear(), 3, 15); // April is month 3 in JavaScript (0-indexed)
    if (now > taxDay) {
      taxDay.setFullYear(taxDay.getFullYear() + 1); // If today's date is past April 15, calculate days until next year's tax day
    }
    const diffTime = Math.abs(taxDay.getTime() - now.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  const auth = useAuth();


  const theme = useTheme()

  function CompanyCards() {
    const [companyFilter, setCompanyFilter] = React.useState<string>('all');
    const auth = useAuth();
    const { data: allCompanies, isLoading, isError, refetch } = useGetNumberOfCompanies();

    const activeCount = React.useMemo(() =>
      allCompanies?.filter((c: any) => c.status?.toLowerCase() === 'active').length || 0,
      [allCompanies]
    );
    const pendingCount = React.useMemo(() =>
      allCompanies?.filter((c: any) => c.status?.toLowerCase() === 'pending').length || 0,
      [allCompanies]
    );
    const totalCount = allCompanies?.length || 0;

    const displayCount = companyFilter === 'active' ? activeCount
      : companyFilter === 'pending' ? pendingCount
      : totalCount;

    const displayLabel = companyFilter === 'active' ? 'Active companies'
      : companyFilter === 'pending' ? 'Pending companies'
      : 'Total companies';

    return (
      <>
        <Grid item xs={12} md={4}>
          <Grid item sx={{ paddingBottom: '10px' }}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                boxShadow: 'none',
                borderRadius: 4
              }}
            >
              <CardHeader
                title={displayLabel}
                subheader='Filter by status'
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CustomAvatar skin='light' variant='rounded' color='primary' sx={{ mr: 4 }}>
                    <Icon icon='mdi:building' width={30} />
                  </CustomAvatar>
                  <Typography variant="h4" component="div" sx={{ mb: '2px' }} fontWeight={800}>
                    {displayCount}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={companyFilter}
                  exclusive
                  onChange={(_, val) => val && setCompanyFilter(val)}
                  size='small'
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, py: 0.5, fontSize: '0.75rem' } }}
                >
                  <ToggleButton value='all'>All ({totalCount})</ToggleButton>
                  <ToggleButton value='active'>Active ({activeCount})</ToggleButton>
                  <ToggleButton value='pending'>Pending ({pendingCount})</ToggleButton>
                </ToggleButtonGroup>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid item sx={{ paddingBottom: '10px' }}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                boxShadow: 'none',
                borderRadius: 4
              }}
            >
              <CardHeader
                title='New Companies'
                subheader='Recently added (pending)'
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CustomAvatar skin='light' variant='rounded' color='success' sx={{ mr: 4 }}>
                    <Icon icon='mdi:office-building-plus' width={30} />
                  </CustomAvatar>
                  <Typography variant="h4" component="div" sx={{ mb: '2px' }} fontWeight={800}>
                    {pendingCount}
                  </Typography>
                </Box>
                {pendingCount > 0 && (
                  <Chip
                    label={`${pendingCount} awaiting activation`}
                    color='warning'
                    size='small'
                    variant='outlined'
                    sx={{ mt: 2 }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    );
  }

  const EcommerceSalesOverview = () => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const infoIconRef = React.useRef(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const { data: numberOfDrivershook, isLoading: isLoadingDrivers, refetch: refetchDrivers } = useGetNumberOfDrivers();
    const { data: numberOfVehicles, isLoading: isLoadingVehicles, refetch: refetchVehicles } = useGetNumberOfVehicles();
    const { data: numberOfEmployees, isLoading, isError, refetch } = useGetNumberOfEmployees();

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    return (
      <Grid item xs={12}>
        <Card
          sx={{
            flex: 1,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.06)} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
            boxShadow: 'none',
            borderRadius: 4
          }}
        >
          <CardHeader
            sx={{ pb: 3.25 }}
            title='Overview'
            subheader='Quick overview of your business'
            titleTypographyProps={{ variant: 'h6' }}
            action={
              <>
                <IconButton aria-label="settings" onClick={handleClick} ref={infoIconRef}>
                  <Icon icon='mdi:info-outline' fontSize={18} />
                </IconButton>
                <Popover
                  id={id}
                  open={open}
                  anchorEl={infoIconRef.current}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  sx={{
                    '& .MuiPopover-paper': {
                      width: {
                        xs: '50%',
                        sm: '20%'
                      },
                      maxWidth: 'none',
                      padding: '0px',
                      borderRadius: '10px',
                    }
                  }}
                >
                  <Typography sx={{
                    p: 2,
                    fontSize: 12,
                    backgroundColor: 'black',
                    color: 'white',
                  }}>
                    This is quick overview of your business and how it's doing.
                  </Typography>
                </Popover>
              </>
            }
          />
          <CardContent>
            <Grid container spacing={6}>
              <Grid item xs={12} sm={4} >
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 2, backgroundColor: alpha(theme.palette.info.main, 0.08) }}>
                  <CustomAvatar skin='light' variant='rounded' color='info' sx={{ mr: 4 }}>
                    <Icon icon='mdi:truck' />
                  </CustomAvatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant='h6' sx={{ fontWeight: 600 }}>
                      {numberOfVehicles ? numberOfVehicles.length : 0}
                    </Typography>
                    <Typography variant='caption'>All Vehicles</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4} >
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 2, backgroundColor: alpha(theme.palette.success.main, 0.08) }}>
                  <CustomAvatar skin='light' variant='rounded' color='success' sx={{ mr: 4 }}>
                    <Icon icon='mdi:steering' />
                  </CustomAvatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant='h6' sx={{ fontWeight: 600 }}>
                      {numberOfDrivershook ? numberOfDrivershook.length : 0}
                    </Typography>
                    <Typography variant='caption'>All Drivers</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4} >
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08) }}>
                  <CustomAvatar skin='light' variant='rounded' color='warning' sx={{ mr: 4 }}>
                    <Icon icon='mdi:account-group' />
                  </CustomAvatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant='h6' sx={{ fontWeight: 600 }}>
                      {numberOfEmployees ? numberOfEmployees.length : 0}
                    </Typography>
                    <Typography variant='caption'>All Employees</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    )
  }

  return (
    <ProtectedRoute>
      {auth?.userInfo?.can_manage === "NEW_USER_BLANK" || auth?.userInfo?.can_manage === "authenticated" || auth?.userInfo?.can_manage !== "admin_manage" ? (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant='h5'>You are not authorized to view this page.</Typography>
          <Typography variant='body1'>Please contact your administrator.</Typography>
        </Box>
      ) : (
        <Box>
          <Grid container spacing={2}>
            <CompanyCards />
            <Grid item xs={12} md={4}>
              <EcommerceSalesOverview />
            </Grid>
            <Grid item xs={12}>
              <UpcomingReminders cardStyles={{ backgroundColor: alpha(theme.palette.warning.main, 0.04), border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}` }} />
            </Grid>
          </Grid>
        </Box>
      )}

    </ProtectedRoute>
  )
}

export default Home
